import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';

const CreativeHeroSection = ({ heroRef, heroInView, staggerContainer, slideInFromLeft }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const floatingAnimation = {
    animate: {
      y: [-20, 20, -20],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const pulseAnimation = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section 
      className="position-relative overflow-hidden text-white"
      ref={heroRef}
      style={{
        minHeight: '100vh',
        background: `
          radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, 
            rgba(79, 195, 247, 0.3) 0%, 
            rgba(26, 35, 126, 0.8) 40%, 
            rgba(13, 27, 42, 1) 100%
          ),
          linear-gradient(135deg, #0d1b2a 0%, #1a237e 50%, #283593 100%)
        `,
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100" style={{ zIndex: 1 }}>
        {/* Floating Orbs */}
        <motion.div
          {...floatingAnimation}
          className="position-absolute rounded-circle"
          style={{
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.2) 0%, transparent 70%)',
            top: '10%',
            right: '10%',
            filter: 'blur(1px)'
          }}
        />
        
        <motion.div
          animate={{
            y: [30, -30, 30],
            x: [0, 20, 0],
            transition: { duration: 8, repeat: Infinity, ease: "easeInOut" }
          }}
          className="position-absolute rounded-circle"
          style={{
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(102, 187, 106, 0.15) 0%, transparent 70%)',
            bottom: '20%',
            left: '5%',
            filter: 'blur(2px)'
          }}
        />

        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
            transition: { duration: 15, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{
            width: '150px',
            height: '150px',
            background: 'conic-gradient(from 0deg, rgba(255, 167, 38, 0.1), rgba(79, 195, 247, 0.1), rgba(255, 167, 38, 0.1))',
            top: '60%',
            right: '20%',
            borderRadius: '50%',
            filter: 'blur(1px)'
          }}
        />

        {/* Geometric Shapes */}
        <motion.div
          animate={{
            rotate: [0, 180, 360],
            transition: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(45deg, rgba(79, 195, 247, 0.3), rgba(26, 35, 126, 0.3))',
            top: '25%',
            left: '15%',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            filter: 'drop-shadow(0 0 20px rgba(79, 195, 247, 0.3))'
          }}
        />

        <motion.div
          animate={{
            rotate: [360, 0],
            y: [0, -20, 0],
            transition: { duration: 12, repeat: Infinity, ease: "easeInOut" }
          }}
          className="position-absolute"
          style={{
            width: '60px',
            height: '60px',
            background: 'linear-gradient(135deg, rgba(255, 167, 38, 0.4), rgba(236, 64, 122, 0.4))',
            bottom: '40%',
            right: '30%',
            transform: 'rotate(45deg)',
            filter: 'drop-shadow(0 0 15px rgba(255, 167, 38, 0.3))'
          }}
        />

        {/* Particle Effects */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              transition: {
                duration: 3 + i * 0.2,
                repeat: Infinity,
                delay: i * 0.1,
                ease: "easeOut"
              }
            }}
            className="position-absolute rounded-circle"
            style={{
              width: `${4 + (i % 3) * 2}px`,
              height: `${4 + (i % 3) * 2}px`,
              background: `rgba(79, 195, 247, ${0.3 + (i % 3) * 0.2})`,
              left: `${10 + (i * 4) % 80}%`,
              bottom: '0%',
              filter: 'blur(0.5px)'
            }}
          />
        ))}
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          backgroundImage: `
            linear-gradient(rgba(79, 195, 247, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79, 195, 247, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: 1
        }}
      />

      {/* Main Content */}
      <Container className="position-relative h-100 d-flex align-items-center" style={{ zIndex: 2, minHeight: '100vh' }}>
        <motion.div
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="w-100"
        >
          <Row className="align-items-center justify-content-center text-center">
            <Col lg={10} xl={8}>
              <motion.div variants={slideInFromLeft}>
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="d-inline-flex align-items-center mb-4 px-4 py-2 rounded-pill"
                  style={{
                    background: 'rgba(79, 195, 247, 0.15)',
                    border: '1px solid rgba(79, 195, 247, 0.3)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <motion.div
                    {...pulseAnimation}
                    className="me-2 rounded-circle"
                    style={{
                      width: '8px',
                      height: '8px',
                      background: '#4fc3f7'
                    }}
                  />
                  <span style={{ color: '#4fc3f7', fontWeight: '600', fontSize: '0.9rem' }}>
                    #1 NetSuite Solution Provider
                  </span>
                </motion.div>

                {/* Main Title */}
                <motion.h1 
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="fw-bold mb-4"
                  style={{
                    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                    lineHeight: '1.2',
                    background: 'linear-gradient(135deg, #ffffff 0%, #4fc3f7 50%, #66bb6a 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textShadow: '0 0 30px rgba(79, 195, 247, 0.3)'
                  }}
                >
                  NetSuite Managed
                  <br />
                  <span style={{
                    background: 'linear-gradient(135deg, #4fc3f7 0%, #ffa726 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Services
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="lead mb-5"
                  style={{
                    fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: '1.6',
                    maxWidth: '700px',
                    margin: '0 auto 3rem auto'
                  }}
                >
                  Empowering businesses with expert NetSuite consulting, seamless administration, 
                  and continuous performance optimization for maximum ROI
                </motion.p>

                {/* Action Buttons */}
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="d-flex flex-column flex-sm-row justify-content-center gap-3 mb-5"
                >
                  <motion.button 
                    className="btn btn-lg px-5 py-3 rounded-pill fw-semibold border-0"
                    style={{
                      background: 'linear-gradient(135deg, #4fc3f7 0%, #66bb6a 100%)',
                      color: 'white',
                      boxShadow: '0 15px 35px rgba(79, 195, 247, 0.3)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    whileHover={{ 
                      scale: 1.05, 
                      boxShadow: '0 20px 40px rgba(79, 195, 247, 0.4)',
                      y: -2
                    }}
                    whileTap={{ scale: 0.98 }}
                    // onMouseEnter={(e) => {
                    //   e.target.style.background = 'linear-gradient(135deg, #66bb6a 0%, #4fc3f7 100%)';
                    // }}
                    // onMouseLeave={(e) => {
                    //   e.target.style.background = 'linear-gradient(135deg, #4fc3f7 0%, #66bb6a 100%)';
                    // }}
                  >
                    <span className="position-relative">
                    <a href="/contact" className="btn  btn-lg px-4">
                    Get Started Today
                    </a>
                     
                      <i className="bi bi-arrow-right ms-2"></i>
                    </span>
                  </motion.button>

                  {/* <motion.button 
                    className="btn btn-lg px-5 py-3 rounded-pill fw-semibold"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                    }}
                    whileHover={{ 
                      scale: 1.05,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(79, 195, 247, 0.5)',
                      y: -2
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <i className="bi bi-play-circle me-2"></i>
                    Watch Demo
                  </motion.button> */}
                </motion.div>

                {/* Stats Row */}
                {/* <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.8 }}
                  className="row g-4 justify-content-center mt-5"
                >
                  {[
                    { number: '95%', label: 'Success Rate', icon: 'bi-check-circle-fill' },
                    { number: '24/7', label: 'Support', icon: 'bi-headset' },
                    { number: '100+', label: 'Projects', icon: 'bi-rocket-takeoff' }
                  ].map((stat, index) => (
                    <Col xs={4} md={2} key={index}>
                      <motion.div
                        whileHover={{ scale: 1.1, y: -5 }}
                        className="text-center p-3 rounded-3"
                        style={{
                          background: 'rgba(255, 255, 255, 0.08)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <i 
                          className={`${stat.icon} d-block mb-2`}
                          style={{ 
                            fontSize: '1.5rem', 
                            color: '#4fc3f7' 
                          }}
                        ></i>
                        <div 
                          className="fw-bold"
                          style={{ 
                            fontSize: '1.2rem', 
                            color: 'white' 
                          }}
                        >
                          {stat.number}
                        </div>
                        <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {stat.label}
                        </small>
                      </motion.div>
                    </Col>
                  ))}
                </motion.div> */}
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>

      {/* Bottom Wave */}
      {/* <div className="position-absolute bottom-0 w-100" style={{ zIndex: 2 }}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1440 120"
          style={{ display: 'block' }}
        >
          <motion.path 
            fill="#ffffff" 
            fillOpacity="1" 
            d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,106.7C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1.2 }}
          />
        </svg>
      </div> */}

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
        style={{ zIndex: 3 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="text-center"
        >
          <div 
            className="rounded-pill d-flex align-items-center justify-content-center mx-auto mb-2"
            style={{
              width: '30px',
              height: '50px',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <motion.div
              animate={{ y: [0, 15, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-circle"
              style={{
                width: '4px',
                height: '8px',
                background: '#4fc3f7'
              }}
            />
          </div>
          <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
            Scroll Down
          </small>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default CreativeHeroSection;